import {useCallback, useState} from 'react';
import {Link, useNavigate} from 'react-router';

import {statsRowT} from '@/lib/types/stats.types';
import {BasicRetailerT} from '@/lib/types/user.types';

import {usdFormatter} from '@/lib/helpers';
import {createDataTableConfig} from '@/lib/helpers/config';
import {readableDateHelper} from '@/lib/helpers/readableDateHelper';

import {useAuth} from '@/providers/components/authProvider';

import {StatsRowComponent} from '@/components';
import Page from '@/components/Page';
import {DataTable} from '@/components/dataTable';

import RetailerSelector from './components/retailerSelector';

function RetailerInvoicesPage() {
  // Hooks
  const navigate = useNavigate();
  const {
    userAccounts: {retailers},
  } = useAuth();

  // State
  const [selectedRetailer, setSelectedRetailer] = useState<BasicRetailerT | null>(retailers?.[0] || null);

  // Stats row data
  const statsRow: statsRowT = [
    {
      title: 'Total Invoices',
      value: 10,
      subtitle: 'This Month',
      icon: 'ri-file-list-3-line',
      color: 'primary',
      change: 1.03,
      cardClass: 'Users-main-card primary',
    },
    {
      title: 'Pending Invoices',
      value: 20,
      subtitle: 'This Month',
      icon: 'ri-time-line',
      color: 'warning',
      change: 0.36,
      cardClass: 'Users-main-card warning',
    },
    {
      title: 'Paid Invoices',
      value: 50,
      subtitle: 'This Month',
      icon: 'ri-check-double-line',
      color: 'success',
      change: 1.28,
      cardClass: 'Users-main-card success',
    },
  ];

  // Data Table Config
  const dataTableConfig = createDataTableConfig({
    table: 'Invoices',
    title: 'Invoice Summary',
    columns: [
      'invoice_id',
      'invoice_due_amount',
      'invoice_status',
      'invoice_due_date',
      'invoice_date_issued',
      'invoice_description',
      'invoice_location_id',
      'invoice_location_type',
    ],
    filters: [
      {
        column: 'invoice_location_type',
        operator: 'eq',
        value: 'retailer',
      },
      {
        column: 'invoice_location_id',
        operator: 'eq',
        value: selectedRetailer?.retailer_id,
      },
    ],
    columnsDisplay: [
      {
        header: 'Invoice ID',
        field: 'invoice_id',
        render: invoice => (
          <Link
            to={`/retailer/invoices/${invoice.invoice_id}`}
            className="text-primary hover:text-primary dark:text-primary"
          >
            {invoice.invoice_id}
          </Link>
        ),
      },
      {
        header: 'Status',
        field: 'invoice_status',
        render: invoice => (
          <span
            className={`badge ${
              invoice.invoice_status === 'success'
                ? 'bg-success/10 text-success'
                : invoice.invoice_status === 'pending'
                  ? 'bg-warning/10 text-warning'
                  : 'bg-danger/10 text-danger'
            }`}
          >
            {invoice.invoice_status}
          </span>
        ),
      },
      {
        header: 'Issued Date',
        field: 'invoice_date_issued',
        render: invoice => readableDateHelper(invoice.invoice_date_issued || ''),
      },
      {
        header: 'Due Date',
        field: 'invoice_due_date',
        render: invoice => readableDateHelper(invoice.invoice_due_date || ''),
      },
      {
        header: 'Total Amount',
        field: 'invoice_due_amount',
        render: invoice => usdFormatter.format(invoice.invoice_due_amount),
      },
      {header: 'Description', field: 'invoice_description'},
    ],
    actionsDisplay: [
      // Pay Icon
      {
        icon: 'ri-money-dollar-circle-line',
        color: 'success',
        permission: () => true,
        handler: () => null,
      },
      // Download Icon
      {
        icon: 'ri-download-line',
        color: 'primary',
        permission: () => true,
        handler: () => null,
      },
      // View Icon
      {
        icon: 'ri-eye-line',
        color: 'info',
        permission: () => true,
        handler: () => null,
      },
    ],
    enabled: !!selectedRetailer?.retailer_id,
  });

  const handleCreateNew = useCallback(() => {
    navigate(`/retailer/invoices/create/${selectedRetailer?.retailer_id}`);
  }, [navigate, selectedRetailer]);

  return (
    <Page currentPage="Retailer Invoices">
      {/* Retailer Selector */}
      <div className="mb-4">
        <RetailerSelector
          retailers={retailers || []}
          selectedRetailer={selectedRetailer}
          setSelectedRetailer={setSelectedRetailer}
        />
      </div>

      <StatsRowComponent stats={statsRow} />

      <DataTable {...dataTableConfig} onAdd={handleCreateNew} />
    </Page>
  );
}

export default RetailerInvoicesPage;
